import axios, { AxiosError, AxiosInstance, CancelTokenSource } from 'axios'
import { AddressSearchResultsData, CandidateAddressSearch } from '../common/candidateAddresses.slice'
import { generateRecaptchaToken } from '../common/recapctcha'
import { ADDRESS_SEARCH_PATH, RECAPTCHA_ENABLED, REVERSE_GEOCODE_SEARCH_PATH } from '../environment'
import RecaptchaRequiredError from '../errors/RecaptchaRequiredError'
import { ApiResponse } from './type'

let source: CancelTokenSource

export const cancelAddressSearchRequest = () => {
  if (source) {
    source.cancel('CANCEL')
  }
}

const AXIOS_CONFIG_ADDRESS_SEARCH = {
  baseURL: ADDRESS_SEARCH_PATH,
  withCredentials: true,
}

const AXIOS_CONFIG_REVERSE_GEOCODING_SEARCH = {
  baseURL: REVERSE_GEOCODE_SEARCH_PATH,
  withCredentials: true,
}

class AddressServiceAPI {
  client: AxiosInstance
  reverseGeocodingClient: AxiosInstance

  constructor() {
    this.client = axios.create(AXIOS_CONFIG_ADDRESS_SEARCH)
    this.reverseGeocodingClient = axios.create(AXIOS_CONFIG_REVERSE_GEOCODING_SEARCH)
  }


  /** ADDRESS AUTO COMPLETE **/

  fetchCandidateAddresses = async (payload: CandidateAddressSearch) => {
    // require recaptcha
    if (RECAPTCHA_ENABLED) {
      const isV3Captcha = !payload.recaptchaV2Code
      const recaptchaToken = await generateRecaptchaToken()
      return await this.fetchCandidateAddressesWithRecaptcha(recaptchaToken, payload.searchTerm, isV3Captcha)
    } else {
      return await this.fetchCandidateAddressesWithoutRecaptcha(payload.searchTerm)
    }
  }

  /**
   * Fetch candidate addresses asynchronously. update valuationData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchCandidateAddressesWithoutRecaptcha = async (searchTerm: string) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.get<ApiResponse<AddressSearchResultsData[]>>('/json', {
        params: {
          searchterm: searchTerm,
        },
        cancelToken: source.token,
      })

      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }


  /**
   * Fetch Valuation Data asynchronously. update valuationData in the redux store with the results.
   *
   * @returns {Promise<void>}
   */

  fetchCandidateAddressesWithRecaptcha = async (captchaCode: string, searchTerm: string, isV3recaptcha: boolean) => {
    try {
      const CancelToken = axios.CancelToken
      source = CancelToken.source()
      const response = await this.client.get<ApiResponse<AddressSearchResultsData[]>>('/jsonWithRecaptcha', {
        params: {
          searchterm: searchTerm,
          [isV3recaptcha ? 'recaptchaV3Key' : 'recaptchaV2Key']: captchaCode,
        },
        cancelToken: source.token,
      })
      return response.data.Output
    } catch (error: any) {
      return this.handleError(error)
    }
  }


  handleError = (error: AxiosError) => {
    if (error && error.response && error.response.data ){
      // V3 recaptcha failed (low score) force V2 recaptcha
      throw new RecaptchaRequiredError('error.response.data.type', 'Recaptcha failed (V3), manual Recaptcha required! (V2)')
    } else if (error.message !== 'CANCEL') {
      throw error
    }
  }

}

const addressServiceAPI = new AddressServiceAPI()

export default addressServiceAPI
